<script setup lang="ts">
import { useNuxtApp } from "#imports";

const { $t } = useNuxtApp();

const mobileIndexOpen = ref(false);

const sectionIndex = [
  { id: "duration", label: $t("howToPlay.section.duration.title") },
  { id: "secret-cards", label: $t("howToPlay.section.secretCards.title") },
  { id: "minute-by-minute", label: $t("howToPlay.section.timeline.title") },
  { id: "step-matchball", label: $t("howToPlay.section.matchball.subtitle") },
  { id: "sanctions", label: $t("howToPlay.section.sanctions.title") },
  { id: "match-chrono", label: $t("howToPlay.section.chrono.title") },
];

const activeId = ref<string>("");
const visibleIds = new Set<string>();

onMounted(() => {
  const observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          visibleIds.add(entry.target.id);
        } else {
          visibleIds.delete(entry.target.id);
        }
      }
      const active = sectionIndex.findLast(({ id }) => visibleIds.has(id));
      activeId.value = active?.id ?? "";
    },
    { rootMargin: "-15% 0px -40% 0px" }
  );

  sectionIndex.forEach(({ id }) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });

  onUnmounted(() => observer.disconnect());
});
</script>

<template>
  <nav class="border-y border-white/10 bg-black/80 backdrop-blur-sm sticky top-[120px] z-20">
    <div class="max-w-7xl mx-auto px-6">
      <!-- Mobile: collapsible -->
      <div class="bigsm:hidden">
        <button
          type="button"
          class="flex items-center justify-between w-full py-4 text-left"
          :aria-expanded="mobileIndexOpen"
          @click="mobileIndexOpen = !mobileIndexOpen"
        >
          <span class="uppercase font-semibold">{{ $t("howToPlay.section.index.label") }}</span>
          <svg
            class="w-4 h-4 text-gray-400 transition-transform duration-200"
            :class="mobileIndexOpen ? 'rotate-180' : ''"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <ol v-if="mobileIndexOpen" class="pb-3 space-y-0 border-t border-white/10">
          <li v-for="item in sectionIndex" :key="item.id">
            <a
              :href="`#${item.id}`"
              class="flex items-center gap-2 py-2 pl-2 text-sm transition-colors"
              :class="
                activeId === item.id ? 'text-primary pointer-events-none' : 'text-gray-400 hover:text-primary'
              "
              @click="mobileIndexOpen = false"
            >
              <span>{{ item.label }}</span>
            </a>
          </li>
        </ol>
      </div>
      <!-- Desktop: row -->
      <ol class="hidden bigsm:flex items-center justify-center">
        <li v-for="item in sectionIndex" :key="item.id">
          <a
            :href="`#${item.id}`"
            class="flex items-center gap-1.5 px-4 py-4 text-sm transition-colors whitespace-nowrap uppercase font-semibold"
            :class="
              activeId === item.id ? 'text-primary pointer-events-none' : 'text-gray-400 hover:text-primary'
            "
          >
            <span>{{ item.label }}</span>
          </a>
        </li>
      </ol>
    </div>
  </nav>
</template>
